import { useTranslation } from 'react-i18next';
import { FormField, FormSelect } from '@/components/FormField';
import type { UserRole } from '../../api/employees';
import type { EmployeeFormData } from './CreateEmployeeForm';

interface ContactFieldsProps {
    formData: Pick<EmployeeFormData, 'firstName' | 'lastName' | 'phoneNumber' | 'email' | 'roleId' | 'isMainContact'>;
    errors: {
        firstName?: string;
        lastName?: string;
        phoneNumber?: string;
        email?: string;
        roleId?: string;
    };
    roles?: UserRole[];
    rolesLoading: boolean;
    handleChange: (field: keyof EmployeeFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    handleToggleMainContact: () => void;
}

export function ContactFields({
    formData,
    errors,
    roles,
    rolesLoading,
    handleChange,
    handleToggleMainContact,
}: ContactFieldsProps) {
    const { t } = useTranslation();

    return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                    label=""
                    labelKey="employees.createForm.firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange('firstName')}
                    error={errors.firstName}
                    required
                />
                <FormField
                    label=""
                    labelKey="employees.createForm.lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange('lastName')}
                    error={errors.lastName}
                    required
                />
            </div>

            <FormField
                label=""
                labelKey="employees.createForm.phoneNumber"
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange('phoneNumber')}
                error={errors.phoneNumber}
            />

            <FormField
                label=""
                labelKey="employees.createForm.email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                error={errors.email}
                required
            />

            <FormSelect
                label=""
                labelKey="employees.createForm.role"
                name="roleId"
                value={formData.roleId}
                onChange={handleChange('roleId')}
                error={errors.roleId}
                disabled={rolesLoading}
                required
            >
                <option value="">{t('employees.createForm.rolePlaceholder')}</option>
                {roles?.map((role: UserRole) => (
                    <option key={role.id} value={role.id}>
                        {role.name}
                    </option>
                ))}
            </FormSelect>

            <div className="flex items-center gap-3">
                <label className="relative inline-flex cursor-pointer items-center">
                    <input
                        type="checkbox"
                        checked={formData.isMainContact}
                        onChange={handleToggleMainContact}
                        className="peer sr-only"
                    />
                    <div
                        className={`
                            h-5 w-9 rounded-full bg-surface-darker
                            after:absolute after:start-[2px] after:top-[2px]
                            after:h-4 after:w-4 after:rounded-full after:bg-white
                            after:transition-all after:content-['']
                            peer-checked:bg-primary peer-checked:after:translate-x-full
                            peer-focus:ring-2 peer-focus:ring-primary/20
                        `}
                    />
                </label>
                <span className="text-sm text-onsurface">
                    {t('employees.createForm.mainContact')}
                </span>
            </div>
        </>
    );
}
